import {
  addProviderDays,
  providerDayToInstant,
  providerDayIsoWeekday,
  isValidTimeZone,
} from "../time/providerCalendar";
import { resolveRounds } from "./resolveRounds";
import type { ResolveRoundsInput, ResolvedRound } from "./types";

/** Explicit, versioned requirements. Absence preserves the legacy scalar schedule. */
export type PickRequirement = {
  requirementId: string;
  startsOn: string;
  endsOn: string | null;
  timeZone: string;
  cadence: "day" | "week";
  weekStartDay: number | null;
  requiredPicks: number;
  revivedRequiredPicks: number | null;
  lockTiming: "firstGame" | "lastGame";
  teamReuseAllowed: boolean;
  inheritPreviousReuse: boolean;
  /** Compatibility field: true is prohibited; game dates must stay within their target range. */
  extendPreviousGames: boolean;
  postponementPolicy: "makeUpNextRound" | "countAsLoss";
};
export type PickSchedule = { version: 1; requirements: PickRequirement[] };
export type ResolvedPickRequirement = PickRequirement & {
  reuseScopeId: string;
};
export type ScheduledRound = ResolvedRound & {
  requirement?: ResolvedPickRequirement;
};

const validDay = (value: unknown): value is string =>
  typeof value === "string" &&
  /^\d{4}-\d{2}-\d{2}$/.test(value) &&
  Number.isFinite(Date.parse(value)) &&
  new Date(value).toISOString().slice(0, 10) === value;

/** Structured field paths; no array-order precedence and no timezone fallback. */
export function validatePickSchedule(
  schedule: unknown,
  endDate?: string | null,
  startDate?: string,
): string[] {
  if (schedule == null) return [];
  if (
    typeof schedule !== "object" ||
    !("version" in schedule) ||
    schedule.version !== 1 ||
    !("requirements" in schedule) ||
    !Array.isArray(schedule.requirements) ||
    schedule.requirements.length < 1 ||
    schedule.requirements.length > 100
  )
    return ["pickSchedule"];
  const errors: string[] = [];
  const ids = new Set<string>();
  const intervals: {
    start: number;
    end: number;
    index: number;
    open: boolean;
    req: PickRequirement;
  }[] = [];
  schedule.requirements.forEach((value: unknown, index: number) => {
    const field = `pickSchedule.requirements.${index}`;
    if (value === null || typeof value !== "object") {
      errors.push(field);
      return;
    }
    const r = value as PickRequirement;
    if (
      typeof r.requirementId !== "string" ||
      !/^[a-zA-Z0-9_-]{1,100}$/.test(r.requirementId) ||
      ids.has(r.requirementId)
    )
      errors.push(`${field}.requirementId`);
    ids.add(r.requirementId);
    if (!validDay(r.startsOn) || (r.endsOn !== null && !validDay(r.endsOn))) {
      errors.push(`${field}.dates`);
      return;
    }
    if (startDate && r.startsOn < startDate) errors.push(`${field}.startsOn`);
    const end = r.endsOn ?? endDate;
    if (end && (end < r.startsOn || (endDate && end > endDate)))
      errors.push(`${field}.endsOn`);
    if (
      !["day", "week"].includes(r.cadence) ||
      (r.weekStartDay !== null &&
        (!Number.isInteger(r.weekStartDay) ||
          r.weekStartDay < 1 ||
          r.weekStartDay > 7))
    )
      errors.push(`${field}.cadence`);
    if (
      !Number.isInteger(r.requiredPicks) ||
      r.requiredPicks < 1 ||
      r.requiredPicks > 1000
    )
      errors.push(`${field}.requiredPicks`);
    if (
      r.revivedRequiredPicks !== null &&
      (!Number.isInteger(r.revivedRequiredPicks) ||
        r.revivedRequiredPicks < 1 ||
        r.revivedRequiredPicks > 1000)
    )
      errors.push(`${field}.revivedRequiredPicks`);
    if (!["firstGame", "lastGame"].includes(r.lockTiming))
      errors.push(`${field}.lockTiming`);
    if (!["makeUpNextRound", "countAsLoss"].includes(r.postponementPolicy))
      errors.push(`${field}.postponementPolicy`);
    if (
      [r.teamReuseAllowed, r.inheritPreviousReuse, r.extendPreviousGames].some(
        (v) => typeof v !== "boolean",
      )
    )
      errors.push(field);
    if (r.extendPreviousGames) errors.push(`${field}.unsupportedExtendedGames`);
    if (r.teamReuseAllowed && (r.inheritPreviousReuse || r.extendPreviousGames))
      errors.push(`${field}.teamReuseAllowed`);
    try {
      if (typeof r.timeZone !== "string" || !r.timeZone)
        throw new Error("zone");
      if (!isValidTimeZone({ timeZone: r.timeZone })) throw new Error("zone");
      intervals.push({
        start: providerDayToInstant({ day: r.startsOn, timeZone: r.timeZone }),
        end: end
          ? providerDayToInstant({
              day: addProviderDays({ day: end, days: 1 }),
              timeZone: r.timeZone,
            })
          : Infinity,
        index,
        open: r.endsOn === null,
        req: r,
      });
    } catch {
      errors.push(`${field}.timeZone`);
    }
  });
  intervals.sort((a, b) => a.start - b.start || a.index - b.index);
  intervals.forEach((r, i) => {
    if (i === 0 && (r.req.inheritPreviousReuse || r.req.extendPreviousGames))
      errors.push(`pickSchedule.requirements.${r.index}.previous`);
    if (r.open && i !== intervals.length - 1)
      errors.push(`pickSchedule.requirements.${r.index}.endsOn`);
    const previous = intervals[i - 1];
    if (r.req.inheritPreviousReuse && previous && previous.open)
      errors.push(`pickSchedule.requirements.${r.index}.previous`);
    if (previous && r.start < previous.end)
      errors.push(`pickSchedule.requirements.${r.index}.overlap`);
  });
  return [...new Set(errors)];
}

export function resolveRequirementReuseScopes(
  schedule: PickSchedule,
): ResolvedPickRequirement[] {
  const requirements = [...schedule.requirements].sort(
    (a, b) =>
      providerDayToInstant({ day: a.startsOn, timeZone: a.timeZone }) -
      providerDayToInstant({ day: b.startsOn, timeZone: b.timeZone }),
  );
  let reuseScopeId = "";
  return requirements.map((requirement) => {
    if (requirement.teamReuseAllowed || !requirement.inheritPreviousReuse)
      reuseScopeId = requirement.requirementId;
    return { ...requirement, reuseScopeId };
  });
}

export function resolveScheduledRounds(
  input: ResolveRoundsInput & {
    pickSchedule?: PickSchedule | null | undefined;
    contestEndDate: string;
  },
): { rounds: readonly ScheduledRound[] } {
  if (!input.pickSchedule) return resolveRounds(input);
  const errors = validatePickSchedule(input.pickSchedule, input.contestEndDate);
  if (errors.length)
    throw new Error(`Invalid pick schedule: ${errors.join(", ")}`);
  const requirements = resolveRequirementReuseScopes(input.pickSchedule);
  const result: ScheduledRound[] = [];
  for (const r of requirements) {
    const reuseScopeId = r.reuseScopeId;
    const start = Math.max(
      input.contestStartsAtMs,
      providerDayToInstant({ day: r.startsOn, timeZone: r.timeZone }),
    );
    const end = Math.min(
      input.contestEndsAtMs,
      providerDayToInstant({
        day: addProviderDays({
          day: r.endsOn ?? input.contestEndDate,
          days: 1,
        }),
        timeZone: r.timeZone,
      }),
    );
    if (end <= start) continue;
    const cut = resolveRounds({
      ...input,
      contestStartsAtMs: start,
      contestEndsAtMs: end,
      providerTimeZone: r.timeZone,
      roundGrouping: r.cadence,
      weekStartDay:
        r.weekStartDay ?? providerDayIsoWeekday({ day: r.startsOn }),
    });
    for (const round of cut.rounds) {
      const windowStart = Math.max(
        start,
        providerDayToInstant({ day: round.anchorDay, timeZone: r.timeZone }),
      );
      const windowEnd = Math.min(
        end,
        providerDayToInstant({
          day: addProviderDays({
            day: round.anchorDay,
            days: r.cadence === "week" ? 7 : 1,
          }),
          timeZone: r.timeZone,
        }),
      );
      result.push({
        ...round,
        startsAtMs: windowStart,
        endsAtMs: windowEnd,
        roundKey: `s1:${new Date(windowStart).toISOString()}:${r.requirementId}`,
        gameIds: [...round.gameIds],
        requirement: { ...r, reuseScopeId },
      });
    }
  }
  result.sort(
    (a, b) =>
      a.startsAtMs - b.startsAtMs || a.roundKey.localeCompare(b.roundKey),
  );
  return { rounds: result.map((r, i) => ({ ...r, displayOrdinal: i + 1 })) };
}

/** Submission time never expands the target round's scheduled game dates. */
export function isGameInScheduledRange(input: {
  kickoffAtMs: number;
  startsOn: string;
  endsOn: string;
  timeZone: string;
}): boolean {
  const start = providerDayToInstant({
    day: input.startsOn,
    timeZone: input.timeZone,
  });
  const end = providerDayToInstant({
    day: addProviderDays({ day: input.endsOn, days: 1 }),
    timeZone: input.timeZone,
  });
  return (
    Number.isFinite(input.kickoffAtMs) &&
    input.kickoffAtMs >= start &&
    input.kickoffAtMs < end
  );
}

/** Carrying history is distinct from allowing a team again; no game credit moves. */
export function canInheritPreviousReuse(
  requirements: readonly PickRequirement[],
  requirementId: string,
): boolean {
  try {
    const ordered = [...requirements].sort(
      (a, b) =>
        providerDayToInstant({ day: a.startsOn, timeZone: a.timeZone }) -
        providerDayToInstant({ day: b.startsOn, timeZone: b.timeZone }),
    );
    const index = ordered.findIndex((r) => r.requirementId === requirementId);
    const current = ordered[index];
    const previous = ordered[index - 1];
    return !!(
      current &&
      previous &&
      previous.endsOn &&
      !current.teamReuseAllowed
    );
  } catch {
    return false;
  }
}
